import {
  ShadcnAutoCompleteFilters,
  ShadcnAutoCompleteSearch,
  ShadcnSelectListFilters,
} from "Explore/Components";
import { seniority_select, state_select } from "Explore/Config/FilterData";
import { Skeleton } from "antd";
import styles from "./FiltersFaculty.module.scss";
import {
  getFacetSearchAuthorInstitutions,
  getFacetSearchAuthorSenority,
  getFacetSearchAuthorStates,
  getSuggestSearchAuthors,
} from "Explore/Services/Author";
import {
  KEYWORD_TIME_FILTER_KEYS,
  ParamsFilterKey,
} from "Config/filterSearchParams";
import { getSuggestKeywords } from "Explore/Services/Common";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import { useEffect, useState } from "react";
import InstitutionFacetSearchPopup from "./InstitutionFacetSearchPopup";
import { ToggleGroup, ToggleGroupItem } from "components/ui/toggle-group";
import { ChevronRight, RotateCcw } from "lucide-react";
import { Button } from "components/ui/button";

const FilterFaculty = ({
  filter: filterProps,
  updateFilter: updateFilterProps,
}) => {
  const { filter: _filter, updateSearchParams: _updateSearchParams, resetFilter } =
    useFilterAndPagination();
  const filter = filterProps || _filter;
  const updateFilter = updateFilterProps || _updateSearchParams;
  const [facetInstitutionModalOpen, setFacetInstitutionModalOpen] =
    useState(false);
  const [facetInstitutions, setFacetInstitutions] = useState({
    data: {},
    hasMore: true,
    isLoading: false,
  });
  const [facetSeniorities, setFacetSeniorities] = useState({
    data: [],
    isLoading: false,
  });

  // Full-text search state
  const [ftsQuery, setFtsQuery] = useState(
    filter[ParamsFilterKey.FULL_TEXT_SEARCH] || ""
  );

  const commitFtsQuery = (value) => {
    const trimmed = value?.trim() || null;
    updateFilter({ [ParamsFilterKey.FULL_TEXT_SEARCH]: trimmed || null });
  };

  useEffect(() => {
    if (
      !!filter[ParamsFilterKey.QUERY] ||
      (!filter[ParamsFilterKey.KEYWORD_QUERY] &&
        !filter[ParamsFilterKey.SENIORITIES])
    ) {
      setFacetInstitutions({ data: {}, isLoading: false });
    } else {
      setFacetInstitutions({ data: {}, isLoading: true });
      getFacetSearchAuthorStates({
        keywordQuery: filter[ParamsFilterKey.KEYWORD_QUERY],
        keywordTimeFilter: filter[ParamsFilterKey.KEYWORD_TIME_FILTER],
        seniority: filter[ParamsFilterKey.SENIORITIES],
        page: 0,
        rowsPerPage: 30,
      }).then((res) => {
        setFacetInstitutions({
          data: { 0: res.results || [] },
          isLoading: false,
          hasMore: res.has_next_page,
        });
      });
    }
  }, [
    filter[ParamsFilterKey.KEYWORD_TIME_FILTER],
    JSON.stringify(filter[ParamsFilterKey.KEYWORD_QUERY]),
    JSON.stringify(filter[ParamsFilterKey.SENIORITIES]),
    JSON.stringify(filter[ParamsFilterKey.QUERY]),
  ]);

  useEffect(() => {
    if (
      !!filter[ParamsFilterKey.QUERY] ||
      (!filter[ParamsFilterKey.KEYWORD_QUERY] &&
        !filter[ParamsFilterKey.STATES])
    ) {
      setFacetSeniorities({ data: [], isLoading: false });
    } else {
      setFacetSeniorities({ data: [], isLoading: true });
      getFacetSearchAuthorSenority({
        keywordQuery: filter[ParamsFilterKey.KEYWORD_QUERY],
        keywordTimeFilter: filter[ParamsFilterKey.KEYWORD_TIME_FILTER],
        top_k: 10,
        states: filter[ParamsFilterKey.STATES],
      }).then((res) => {
        setFacetSeniorities({ data: res || [], isLoading: false });
      });
    }
  }, [
    filter[ParamsFilterKey.KEYWORD_TIME_FILTER],
    JSON.stringify(filter[ParamsFilterKey.STATES]),
    JSON.stringify(filter[ParamsFilterKey.KEYWORD_QUERY]),
    JSON.stringify(filter[ParamsFilterKey.QUERY]),
  ]);

  const onChangeFacetSearch = (checkedValue, type) => {
    const current = filter[type] || [];
    let next;
    if (current.includes(checkedValue)) {
      next = current.filter(v => v !== checkedValue);
    } else {
      next = [...current, checkedValue];
    }
    updateFilter({
      [type]: next.length === 0 ? null : next,
    });
  };

  const renderFacetLoading = () => {
    return Array.from({ length: 10 }).map((_, i) => {
      return (
        <div className={styles["facet-item"]} key={i}>
          <Skeleton.Input
            active
            block
            style={{ height: "20px", marginBottom: "2px" }}
          />
        </div>
      );
    });
  };

  return (
    <>
      <div className="flex flex-col gap-8 p-6 bg-white rounded-lg shadow-sm border border-gray-100 h-fit xl:sticky xl:top-[90px] xl:max-h-[calc(100vh-100px)] xl:overflow-y-auto xl:mt-[96px] w-full xl:max-w-[300px]">

        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Filters</h3>
          <button
            onClick={resetFilter}
            className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1.5 transition-colors"
          >
            <RotateCcw size={12} />
            Clear filters
          </button>
        </div>

        {/* Full-text Search */}
        <div className="flex flex-col gap-3">
          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
            Search
          </label>
          <textarea
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Search by name, department, field... (Enter to search, Shift+Enter for new line)"
            value={ftsQuery}
            onChange={(e) => {
              setFtsQuery(e.target.value);
              if (!e.target.value) commitFtsQuery("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                commitFtsQuery(ftsQuery);
              }
            }}
          />
        </div>

        {/* Faculty Members */}
        <div className="flex flex-col gap-3">
          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
            Faculty Members
          </label>
          <ShadcnAutoCompleteSearch
            placeholder={"Search by faculty"}
            functionSearch={getSuggestSearchAuthors}
            filters={filter}
            setFilters={updateFilter}
            entity={ParamsFilterKey.QUERY}
          />
        </div>

        {/* Research Interests */}
        <div className="flex flex-col gap-3">
          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
            Research Interests
          </label>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-gray-500 font-medium">Type:</span>
            <ToggleGroup
              type="single"
              value={filter[ParamsFilterKey.KEYWORD_TIME_FILTER]}
              onValueChange={(val) => {
                if (val) updateFilter({ [ParamsFilterKey.KEYWORD_TIME_FILTER]: val });
              }}
              className="w-full border border-gray-200 rounded-md p-1 bg-gray-100 flex"
            >
              {Object.keys(KEYWORD_TIME_FILTER_KEYS).map((key) => (
                <ToggleGroupItem
                  key={key}
                  value={key}
                  className="flex-1 h-7 px-3 text-[12px] font-medium data-[state=on]:bg-white data-[state=on]:shadow-sm data-[state=on]:text-[#0f3460] text-gray-600 hover:text-gray-900 transition-all"
                >
                  {KEYWORD_TIME_FILTER_KEYS[key]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          <ShadcnAutoCompleteFilters
            placeholder="Search research interests"
            functionSearch={getSuggestKeywords}
            filters={filter}
            setFilters={updateFilter}
            entity={ParamsFilterKey.KEYWORD_QUERY}
            getFirstItemOnEnter
          />
        </div>

        {/* Academic Age */}
        <div className="flex flex-col gap-3">
          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
            Academic Age
          </label>
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {facetSeniorities.isLoading ? (
              renderFacetLoading()
            ) : (
              (!!facetSeniorities.data.length
                ? facetSeniorities.data.map((e) => ({
                  label: seniority_select.find((s) => s.value == `${e.seniority}`)?.label || e.seniority,
                  value: `${e.seniority}`,
                  count: e.no_results
                }))
                : seniority_select.map(s => ({ ...s, count: null }))
              ).map((opt) => {
                const isChecked = (filter[ParamsFilterKey.SENIORITIES] || []).includes(opt.value);
                return (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 accent-primary"
                      checked={isChecked}
                      onChange={() => onChangeFacetSearch(opt.value, ParamsFilterKey.SENIORITIES)}
                    />
                    <span className="flex-1 truncate">{opt.label}</span>
                    {opt.count != null && <span className="text-gray-400 text-xs">{opt.count}</span>}
                  </label>
                )
              })
            )}
          </div>
        </div>

        {/* State */}
        <div className="flex flex-col gap-3">
          <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
            State
          </label>
          <ShadcnSelectListFilters
            placeholder={"Choose a state"}
            options={state_select}
            filters={filter}
            setFilters={updateFilter}
            entity={ParamsFilterKey.STATES}
          />
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto mt-2">
            {facetInstitutions.isLoading ? (
              renderFacetLoading()
            ) : (
              <>
                {!!facetInstitutions.data[0]?.length && (
                  <>
                    {facetInstitutions.data[0].slice(0, 10).map((e) => {
                      const optValue = e.state;
                      const optLabel = state_select.find((s) => s.value === e.state)?.label || e.state;
                      const isChecked = (filter[ParamsFilterKey.STATES] || []).includes(optValue);
                      return (
                        <label key={optValue} className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 accent-primary"
                            checked={isChecked}
                            onChange={() => onChangeFacetSearch(optValue, ParamsFilterKey.STATES)}
                          />
                          <span className="flex-1 truncate">{optLabel}</span>
                          <span className="text-gray-400 text-xs">{e.no_results}</span>
                        </label>
                      );
                    })}
                    <Button
                      variant="ghost"
                      className="w-full justify-between mt-2 text-gray-500 h-8"
                      onClick={() => setFacetInstitutionModalOpen(true)}
                    >
                      See all
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <InstitutionFacetSearchPopup
        open={facetInstitutionModalOpen}
        setOpen={setFacetInstitutionModalOpen}
        data={facetInstitutions}
        setData={setFacetInstitutions}
      />
    </>
  );
};

export default FilterFaculty;
